import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";
import { Plug } from "lucide-react";

import { Chat } from "@/api/chat";
import { Overview } from "@/api/resources";
import { cn } from "@/lib/cn";
import { waitingCount } from "@/lib/decide";
import { countLabel } from "@/lib/unread";
import { useKeyboardInset } from "@/lib/useKeyboardInset";

/**
 * «Решить / Обсуждения / ●Записать / Проекты / Агенты / Знания» — Spec 022 (round 2,
 * agreed by both agents), after Spec 020 D5, DS `BottomNav`. Spec 027 added «Агенты»
 * before «Знания» (Kostya, 30.09: «просто новый раздел как работа»); the others kept
 * their places.
 *
 * Split by the question a person asks, not by entity. «Записать» is the one
 * yellow control on every screen with this bar (D2): a round button in the
 * middle that opens the quick capture. The active tab is the text colour and a
 * bold label, never yellow. The «Решить» counter is what waits for me — the
 * same cards the screen shows; the «Обсуждения» counter is the new messages
 * for me in every discussion, by the read marks (stage 2, task a60d4d9a).
 */

/** Brand icons, brandbook sheet 3: 24 grid, 2px line, square caps, mitred corners. */
const ICON: Record<"now" | "chats" | "capture" | "work" | "projects" | "agents" | "knowledge", ReactNode> = {
  now: (
    <>
      <circle cx={12} cy={12} r={8} />
      <circle cx={12} cy={12} r={3} fill="currentColor" stroke="none" />
    </>
  ),
  chats: <path d="M4 5H20V16H11L7 20V16H4Z" />,
  capture: <path d="M4 4H20V20H4Z M12 8V16 M8 12H16" />,
  work: <path d="M4 6H5 M9 6H20 M4 12H5 M9 12H20 M4 18H5 M9 18H20" />,
  projects: <path d="M3 5H10L12 7H21V19H3Z" />,
  // Spec 027: a head with an antenna — an agent that works on its own.
  agents: <path d="M5 8H19V19H5Z M12 8V4 M9 12V14 M15 12V14" />,
  knowledge: <path d="M12 6V19 M12 6H4V18H12 M12 6H20V18H12" />,
};

export function BrandIcon({ name, size = 24 }: { name: keyof typeof ICON; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
    >
      {ICON[name]}
    </svg>
  );
}

const TABS = [
  { to: "/", label: "Решить", icon: "now", end: true },
  { to: "/chat", label: "Обсуждения", icon: "chats", end: false },
  null, // ●Записать
  { to: "/projects", label: "Проекты", icon: "projects", end: false },
  // Spec 035 v0.4: agents are staff of units — «Отделы» took the place of «Агенты».
  { to: "/departments", label: "Отделы", icon: "agents", end: false },
  { to: "/knowledge", label: "Знания", icon: "knowledge", end: false },
] as const;

// On a computer the connections get their own place in the sidebar: tucked in the
// profile, Kostya decided they could not be set up from a PC (PC audit, point 3).
const DESK_ONLY = [
  { to: "/settings/agents", label: "Подключения", icon: "connections", lucide: Plug, end: false },
] as const;

const LABEL = "text-[11px] font-semibold leading-[14px]";

/**
 * New messages for me in all discussions (task a60d4d9a): one small page of
 * «Обсуждения» carries the total. The bar and the sidebar share the query; a
 * discussion marked read refreshes it with every other «chat threads» query.
 */
export function useUnreadTotal(): number {
  const threads = useQuery({
    queryKey: ["chat", "threads", "total"],
    queryFn: () => Chat.threads(undefined, 1),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  return threads.data?.unread_total ?? 0;
}

export function BottomNav({ onCapture }: { onCapture: () => void }) {
  // The same query as «Сейчас»; a GET never moves the «seen» marker — only the
  // screen itself posts that, for the snapshot it showed.
  const overview = useQuery({ queryKey: ["overview"], queryFn: Overview.get, staleTime: 60_000 });
  const waiting = waitingCount(overview.data);
  const unread = useUnreadTotal();
  // Pinned to the screen, not to the page: it stays put while the list scrolls.
  // With the keyboard open it steps aside, or it would ride the keyboard over
  // the field being typed in.
  const keyboard = useKeyboardInset();
  if (keyboard > 0) return null;

  return (
    <nav
      aria-label="Разделы"
      className="fixed inset-x-0 bottom-0 z-nav mx-auto grid w-full max-w-app grid-cols-6 items-end border-t border-p-border bg-p-surface px-1 pb-[max(env(safe-area-inset-bottom),6px)] pt-1.5 lg:hidden"
    >
      {TABS.map((tab) =>
        tab === null ? (
          <button
            key="capture"
            type="button"
            onClick={onCapture}
            className="flex flex-col items-center gap-0.5 py-1 text-p-text"
          >
            <span className="-mt-[18px] flex h-12 w-12 items-center justify-center rounded-full bg-p-accent text-p-on-accent shadow-[0_0_0_4px_var(--p-surface)] press-surface">
              <BrandIcon name="capture" size={26} />
            </span>
            <span className={LABEL}>Записать</span>
          </button>
        ) : (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                "flex min-h-touch flex-col items-center justify-end gap-0.5 py-1",
                isActive ? "text-p-text [&>span:last-child]:font-bold" : "text-p-text-muted",
              )
            }
          >
            <span className="relative flex">
              <BrandIcon name={tab.icon} />
              {tab.icon === "now" && waiting > 0 && (
                <span
                  className="absolute -right-2.5 -top-1 h-[18px] min-w-[18px] rounded-full bg-p-needs px-[5px] text-center text-[11px] font-bold leading-[18px] text-p-surface"
                  aria-label={`ждёт тебя: ${waiting}`}
                >
                  {waiting}
                </span>
              )}
              {tab.icon === "chats" && unread > 0 && (
                <span
                  className="absolute -right-2.5 -top-1 h-[18px] min-w-[18px] rounded-full bg-p-needs px-[5px] text-center text-[11px] font-bold leading-[18px] text-p-surface"
                  aria-label={`новых сообщений: ${unread}`}
                >
                  {countLabel(unread)}
                </span>
              )}
            </span>
            <span className={LABEL}>{tab.label}</span>
          </NavLink>
        ),
      )}
    </nav>
  );
}

/**
 * The same sections on a wide screen (≥1024 px) — Spec 021, after the «с нуля»
 * canvas's desktop artboard: a 232 px column on the left with the logo, the
 * sections, the one yellow «Записать» and the profile. The bottom bar is for
 * phones only; a computer gets a computer's layout.
 */
export function SideNav({ onCapture, logo, profile }: { onCapture: () => void; logo: ReactNode; profile: ReactNode }) {
  const overview = useQuery({ queryKey: ["overview"], queryFn: Overview.get, staleTime: 60_000 });
  const waiting = waitingCount(overview.data);
  const unread = useUnreadTotal();
  return (
    // 1024–1279 px: icons only, so an open task keeps its width (PC audit 28.09, point 1).
    <aside className="sticky top-0 hidden h-screen w-[64px] shrink-0 flex-col gap-6 py-8 lg:flex xl:w-[232px]">
      <div className="px-3">{logo}</div>
      <nav aria-label="Разделы" className="flex flex-col gap-1">
        {[...TABS.filter((tab) => tab !== null), ...DESK_ONLY].map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            aria-label={tab.label}
            title={tab.label}
            className={({ isActive }) =>
              cn(
                "press-surface relative flex min-h-11 items-center gap-3 rounded-compact px-3 text-cell",
                isActive ? "bg-p-surface font-bold text-p-text shadow-[0_0_0_1px_var(--p-border)]" : "text-p-text-muted hover:text-p-text",
              )
            }
          >
            {"lucide" in tab ? <tab.lucide className="h-[22px] w-[22px] shrink-0" aria-hidden="true" /> : <BrandIcon name={tab.icon} size={22} />}
            <span className="hidden flex-1 xl:inline">{tab.label}</span>
            {tab.icon === "now" && waiting > 0 && (
              <span
                className="absolute right-0.5 top-0.5 h-5 min-w-5 rounded-full bg-p-needs px-1.5 text-center text-[12px] font-bold leading-5 text-p-surface xl:static"
                aria-label={`ждёт тебя: ${waiting}`}
              >
                {waiting}
              </span>
            )}
            {tab.icon === "chats" && unread > 0 && (
              <span
                className="absolute right-0.5 top-0.5 h-5 min-w-5 rounded-full bg-p-needs px-1.5 text-center text-[12px] font-bold leading-5 text-p-surface xl:static"
                aria-label={`новых сообщений: ${unread}`}
              >
                {countLabel(unread)}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <button
        type="button"
        onClick={onCapture}
        aria-label="Записать"
        title="Записать"
        className="press-surface mx-1 flex h-12 items-center justify-center gap-2 rounded-xl bg-p-accent text-[16px] font-semibold text-p-on-accent"
      >
        <BrandIcon name="capture" size={22} />
        <span className="hidden xl:inline">Записать</span>
      </button>
      <div className="mt-auto px-1">{profile}</div>
    </aside>
  );
}
